import { isIssueLookup } from "./issue-search";

export type IssueTab = "human" | "active" | "backlog" | "all" | "done" | "archived";
export const WAITING_SCOPES = ["everyone", "mine"] as const;
export type WaitingScope = (typeof WAITING_SCOPES)[number];

export const GROUPINGS = [
  "status",
  "type",
  "priority",
  "team",
  "repo",
  "reviewer",
  "reporter",
  "label",
  "sla",
  "waitingOn",
  "none",
] as const;
export type IssueGrouping = (typeof GROUPINGS)[number];

export interface IssueTabDef {
  value: IssueTab;
  label: string;
  /** What the tab shows. A status or status type picked in the filters replaces the tab's own. */
  filters: Record<string, string>;
  /** Grouping until the person picks one. */
  groupBy: IssueGrouping;
  /** Listed under More rather than as a tab. */
  more?: boolean;
}

export const ISSUE_TABS: IssueTabDef[] = [
  // A waiting sub-issue lists its top-level parent, once, like the other tabs.
  {
    value: "human",
    label: "Human required",
    filters: { needsHuman: "1", rollUp: "1" },
    groupBy: "waitingOn",
  },
  {
    value: "active",
    label: "Active",
    filters: { statusCategory: "backlog,unstarted,started" },
    groupBy: "status",
  },
  { value: "backlog", label: "Backlog", filters: { statusCategory: "backlog" }, groupBy: "status" },
  { value: "all", label: "All", filters: {}, groupBy: "status", more: true },
  { value: "done", label: "Done", filters: { status: "done" }, groupBy: "status", more: true },
  { value: "archived", label: "Archived", filters: { archived: "closed" }, groupBy: "status", more: true },
];

export const ISSUE_TAB_VALUES = ISSUE_TABS.map((tab) => tab.value);
export const DEFAULT_ISSUE_TAB: IssueTab = "active";

/** Query keys read here. Everything else in the URL passes through as an issue filter. */
const PAGE_KEYS = new Set(["tab", "scope", "groupBy"]);

// These read `URLSearchParams` from `useSearchParams`, not nuqs: the list keeps
// rendering under the issue modal's background location, while nuqs follows
// `window.location`, which is the issue's URL then.

export function issueTab(params: URLSearchParams): IssueTabDef {
  const value = params.get("tab");
  return (
    ISSUE_TABS.find((tab) => tab.value === value) ??
    ISSUE_TABS.find((tab) => tab.value === DEFAULT_ISSUE_TAB)!
  );
}

export function waitingScope(params: URLSearchParams): WaitingScope {
  return params.get("scope") === "mine" ? "mine" : "everyone";
}

/** The picked grouping, or the tab's own. `none` lists the issues flat. */
export function issueGrouping(params: URLSearchParams, fallback: IssueGrouping = "status"): IssueGrouping {
  const value = params.get("groupBy");
  return GROUPINGS.find((key) => key === value) ?? fallback;
}

/** The `GET /issues` query for a tab: the person's filters, then the tab's own. */
export function issueQuery(
  tab: IssueTabDef,
  params: URLSearchParams,
  scope: WaitingScope,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of params.entries()) {
    if (!PAGE_KEYS.has(key)) out[key] = value;
  }
  const groupBy = issueGrouping(params, tab.groupBy);
  if (groupBy !== "none") out.groupBy = groupBy;
  // A number, identifier, or id names one issue. Find it even when it is a
  // sub-issue, done, canceled, or archived. Title search stays on this view.
  if (isIssueLookup(out.q ?? "")) {
    if (!out.archived) out.archived = "include";
    return out;
  }
  const pickedStatus = Boolean(out.status || out.statusCategory);
  for (const [key, value] of Object.entries(tab.filters)) {
    if ((key === "status" || key === "statusCategory") && pickedStatus) continue;
    out[key] = value;
  }
  if (tab.value === "human" && scope === "mine") out.waitingFor = "me";
  // Sub-issues sit on their parent unless the Relations filter asks for them.
  if (!out.hasParent) out.hasParent = "0";
  return out;
}

/**
 * What a badge counts: the tab with the person's filters, without the search
 * box, grouping, or order. The search goes first, so an identifier lookup
 * cannot drop the tab's own filters.
 */
export function countQuery(
  tab: IssueTabDef,
  params: URLSearchParams,
  scope: WaitingScope,
): Record<string, string> {
  const unsearched = new URLSearchParams(params);
  unsearched.delete("q");
  const {
    groupBy: _groupBy,
    orderBy: _orderBy,
    direction: _direction,
    ...filters
  } = issueQuery(tab, unsearched, scope);
  return filters;
}

export interface IssuePreset {
  id: string;
  label: string;
  filters: Record<string, string>;
}

/** Occasional views, applied as ordinary filter chips on the current tab. */
export const ISSUE_PRESETS: IssuePreset[] = [
  { id: "mine", label: "My issues", filters: { involves: "me" } },
  { id: "slaAtRisk", label: "SLA at risk", filters: { slaStatus: "at_risk,breached" } },
  { id: "needsScope", label: "Needs scope", filters: { status: "needsScope" } },
];

export function presetActive(preset: IssuePreset, params: URLSearchParams): boolean {
  return Object.entries(preset.filters).every(([key, value]) => params.get(key) === value);
}

/** Turns a preset's filters on, replacing those keys, or off again. */
export function togglePreset(preset: IssuePreset, params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);
  const on = !presetActive(preset, params);
  for (const [key, value] of Object.entries(preset.filters)) {
    if (on) next.set(key, value);
    else next.delete(key);
  }
  return next;
}
